"use client";

import React, { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import { useLocation, useNavigate } from "react-router-dom";
import { createDome, fetchTimezones } from "../../api/dome.api";
import { useJsApiLoader } from "@react-google-maps/api";

const libraries = ["places"];

const CreateDome = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const { hostData: host } = location.state || {};
  console.log("🚀 ~ CreateDome ~ host:", host);

  const [domeData, setDomeData] = useState({
    domeId: "",
    domeName: "",
    domeDescription: "",
    price: "",
    location: "",
    coordinatesLat: "",
    coordinatesLng: "",
    timezone: "",
  });

  const [timezones, setTimezones] = useState([]);

  useEffect(() => {
    fetchTimezones().then(setTimezones);
  }, []);

  const [coverImage, setCoverImage] = useState(null);
  const [images, setImages] = useState([]);
  const [coverPreview, setCoverPreview] = useState(null);
  const [previewUrls, setPreviewUrls] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: libraries,
  });

  const placeAutocompleteRef = useRef(null);
  const ignoreRef = useRef(false);

  useEffect(() => {
    if (isLoaded && placeAutocompleteRef.current && !ignoreRef.current) {
      (async () => {
        try {
          // Import the new Places library
          const { PlaceAutocompleteElement } = await window.google.maps.importLibrary("places");

          // Check if already appended to avoid duplicates if strict mode mounts twice
          if (placeAutocompleteRef.current.querySelector("gmp-place-autocomplete")) return;

          // Create the element
          const autocompleteElement = new PlaceAutocompleteElement();
          autocompleteElement.id = "place-autocomplete-input";
          // Apply some basic styling to match bootstrap form-control if possible, 
          // or just ensure it fills width. Web components encapsulate styles so class might not affect inner.
          // autocompleteElement.className = "form-control"; 

          // Append to our container
          placeAutocompleteRef.current.appendChild(autocompleteElement);

          // Add event listener for place selection
          autocompleteElement.addEventListener("gmp-places-select", async (event) => {
            const place = event?.detail?.place;
            if (!place) return;

            await place.fetchFields({ fields: ["displayName", "formattedAddress", "location"] });

            const address = place.formattedAddress || place.displayName || "";
            const lat = place.location?.lat?.();
            const lng = place.location?.lng?.();

            // Keep the input displaying the resolved address
            autocompleteElement.value = address;

            setDomeData((prev) => ({
              ...prev,
              location: address,
              coordinatesLat: typeof lat === "number" ? lat : "",
              coordinatesLng: typeof lng === "number" ? lng : "",
              // Note: Timezone detection is now handled automatically and robustly
              // on the backend using the offline geo-tz package based on these coordinates.
            }));
          });

          ignoreRef.current = true;

        } catch (e) {
          console.error("Failed to load Places API:", e);
        }
      })();
    }
  }, [isLoaded]);

  // Generate preview for cover image
  useEffect(() => {
    if (coverImage) {
      const url = URL.createObjectURL(coverImage);
      setCoverPreview(url);
      return () => URL.revokeObjectURL(url);
    } else {
      setCoverPreview(null);
    }
  }, [coverImage]);

  // Generate preview URLs when additional images change
  useEffect(() => {
    const urls = images.map((file) => ({
      file,
      url: URL.createObjectURL(file),
    }));
    setPreviewUrls(urls);

    // Cleanup URLs on unmount
    return () => urls.forEach(({ url }) => URL.revokeObjectURL(url));
  }, [images]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setDomeData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCoverChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Cover image is too large. Please upload a file smaller than 5MB.');
        e.target.value = '';
        return;
      }
      setCoverImage(file);
    }
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const oversized = files.filter(f => f.size > 5 * 1024 * 1024);
    if (oversized.length > 0) {
      toast.error(`${oversized.length} image(s) exceed 5MB and were skipped.`);
    }
    const valid = files.filter(f => f.size <= 5 * 1024 * 1024);
    // Append new images, don't overwrite
    setImages((prev) => [...prev, ...valid]);
  };

  const handleRemoveImage = (index) => {
    const updatedImages = [...images];
    updatedImages.splice(index, 1);
    setImages(updatedImages);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      if (!coverImage) {
        setError("Please upload a cover image.");
        setLoading(false);
        return;
      }

      if (images.length === 0) {
        setError("Please upload at least one additional image.");
        setLoading(false);
        return;
      }

      const formData = new FormData();
      formData.append("hostRef", host._id);

      // Append cover image
      formData.append("coverImage", coverImage);

      // Ensure we have a non-empty location value, using the Places input as fallback
      let locationToSend = domeData.location?.trim() || "";
      const placeInput = document.getElementById("place-autocomplete-input");
      const placeValue = placeInput?.value?.trim();
      if (!locationToSend && placeValue) {
        locationToSend = placeValue;
      }

      if (!locationToSend) {
        setError("Please search and select a location.");
        setLoading(false);
        return;
      }

      formData.append("location", locationToSend);

      // Append other dome fields
      Object.entries(domeData).forEach(([key, value]) => {
        // location is handled separately above
        if (key === "location") return;

        // For coordinates, only send if we have a valid numeric value;
        // otherwise let the backend geocode from the address.
        if (
          (key === "coordinatesLat" || key === "coordinatesLng") &&
          (value === "" || value === null || Number.isNaN(Number(value)))
        ) {
          return;
        }

        // Skip empty price if it was commented out/not provided
        if (key === "price" && (value === "" || value === null)) {
          return;
        }

        // Skip empty timezone
        if (key === "timezone" && (value === "" || value === null)) {
          return;
        }

        console.log(`📎 Appending to formData: ${key} = ${value}`);
        formData.append(key, value);
      });

      // Append additional images
      images.forEach((file) => formData.append("images", file));

      const response = await createDome(formData);
      console.log("✅ Dome created:", response);
      navigate(`/host/${host._id}/dome`);
    } catch (err) {
      console.error(err);
      setError(err?.message || "Failed to create dome");
    } finally {
      setLoading(false);
    }
  };

  if (!isLoaded) {
    return <div>Loading Map Scripts...</div>;
  }

  return (
    <div className="container mt-4 px-3 px-md-0">
      <div className="d-flex align-items-center mb-4">
        <button
          onClick={() => navigate(-1)}
          className="btn btn-outline-secondary me-3"
        >
          <i className="fas fa-arrow-left me-2"></i>Back
        </button>
        <h2 className="fs-24 fs-md-32 mb-0">
          Create Dome for:{" "}
          <span className="text-primary">
            {host?.companyDisplayName || host?.companyName}
          </span>
        </h2>
      </div>

      <form onSubmit={handleSubmit} className="mt-3">
        <div className="mb-3">
          <label className="form-label">Dome ID</label>
          <input
            type="text"
            name="domeId"
            value={domeData.domeId}
            onChange={handleChange}
            className="form-control"
            required
          />
        </div>

        <div className="mb-3">
          <label className="form-label">Dome Name</label>
          <input
            type="text"
            name="domeName"
            value={domeData.domeName}
            onChange={handleChange}
            className="form-control"
            required
          />
        </div>

        <div className="mb-3">
          <label className="form-label">Dome Description</label>
          <textarea
            name="domeDescription"
            value={domeData.domeDescription}
            onChange={handleChange}
            className="form-control"
            rows={3}
          ></textarea>
        </div>

        <div className="row">
          {/* <div className="col-md-6 mb-3">
            <label className="form-label">Price</label>
            <input
              type="number"
              name="price"
              value={domeData.price}
              onChange={handleChange}
              className="form-control"
              min={0}
              required
            />
          </div> */}

          {/* <div className="col-md-6 mb-3">
            <label className="form-label">Offer Percent</label>
            <input
              type="number"
              name="offerPercent"
              value={domeData.offerPercent}
              onChange={handleChange}
              className="form-control"
              min={0}
              max={100}
            />
          </div> */}
        </div>

        <div className="mb-3">
          <label className="form-label">Location (Search & Select)</label>
          <div ref={placeAutocompleteRef} style={{ minHeight: '40px' }} className="mb-2"></div>
          {/* Visual confirmation and validation binding */}
          {domeData.location && <div className="text-success small">Selected: {domeData.location}</div>}
          <input
            type="text"
            name="location"
            value={domeData.location}
            readOnly
            className="form-control d-none"
            required
          />
          <div className="form-text">Search for a location and select it from the dropdown.</div>
        </div>

        {/* Timezone */}
        <div className="mb-3">
          <label className="form-label">Timezone</label>
          <select
            name="timezone"
            value={domeData.timezone}
            onChange={handleChange}
            className="form-control"
            required
          >
            <option value="">-- Select Timezone --</option>
            {timezones.map((tz) => (
              <option key={tz} value={tz}>
                {tz}
              </option>
            ))}
          </select>
          {/* <div className="form-text">
            Choose the physical timezone where the dome is located.
          </div> */}
        </div>

        {/* Cover Image */}
        <div className="mb-3">
          <label className="form-label">Cover Image</label>
          <input
            type="file"
            accept="image/*"
            onChange={handleCoverChange}
            className="form-control"
            required
          />
          <div className="form-text">Accepted formats: JPG, PNG, WEBP &middot; Max size: 5MB</div>
          {coverPreview && (
            <div className="mt-2" style={{ width: 200, height: 120 }}>
              <img
                src={coverPreview}
                alt="cover preview"
                className="img-fluid w-100 h-100"
                style={{ objectFit: "cover", borderRadius: 5 }}
              />
            </div>
          )}
        </div>

        {/* Additional Images */}
        <div className="mb-3">
          <label className="form-label">Additional Dome Images</label>
          <input
            type="file"
            multiple
            accept="image/*"
            onChange={handleFileChange}
            className="form-control"
            required
          />
          <div className="form-text">Accepted formats: JPG, PNG, WEBP &middot; Max 10 images &middot; Max size: 5MB per image</div>

          {previewUrls.length > 0 && (
            <div className="mt-3 d-flex flex-wrap gap-3">
              {previewUrls.map(({ url }, idx) => (
                <div
                  key={idx}
                  className="border rounded position-relative overflow-hidden"
                  style={{
                    width: "120px",
                    height: "100px",
                    backgroundColor: "#f8f9fa",
                  }}
                >
                  <img
                    src={url}
                    alt={`preview-${idx}`}
                    className="img-fluid w-100 h-100"
                    style={{ objectFit: "cover" }}
                  />
                  <button
                    type="button"
                    className="btn btn-sm btn-danger position-absolute top-0 end-0 m-1 px-2 py-0"
                    style={{
                      borderRadius: "50%",
                      fontSize: "0.8rem",
                      lineHeight: "1rem",
                    }}
                    onClick={() => handleRemoveImage(idx)}
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {error && <div className="alert alert-danger">{error}</div>}

        <button type="submit" className="btn btn-primary" disabled={loading}>
          {loading ? "Creating..." : "Create Dome"}
        </button>
      </form>
    </div>
  );
};

export default CreateDome;
